/**
 * Airborne Apparel Company
 * Free shipping progress bar for Shopify / Impulse
 */

(() => {
  'use strict';

  const SELECTOR = '[data-free-shipping-progress]';
  const CART_ENDPOINT = `${window.Shopify?.routes?.root || '/'}cart.js`;

  let updateTimer = null;
  let requestInProgress = false;
  let updateRequested = false;

  /**
   * Formats a cent-based amount using the shop currency.
   *
   * @param {number} cents
   * @param {string} currency
   * @returns {string}
   */
  function formatMoney(cents, currency) {
    const amount = Number(cents) / 100;

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency: currency || window.Shopify?.currency?.active || 'USD'
        }
      ).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  /**
   * Updates every progress bar currently present in the DOM.
   *
   * @param {Object} cart
   */
  function renderProgressBars(cart) {
    const progressBars = document.querySelectorAll(SELECTOR);

    progressBars.forEach((bar) => {
      const threshold = Number(bar.dataset.threshold || 0);
      const currency =
        cart.currency ||
        bar.dataset.currency ||
        window.Shopify?.currency?.active ||
        'USD';

      /*
       * total_price reflects the current cart total after cart discounts.
       * Change this to cart.items_subtotal_price if your free-shipping
       * rule is based on the merchandise subtotal before discounts.
       */
      const cartTotal = Number(
  cart.items_subtotal_price || cart.total_price || 0
);

      if (!threshold) {
        bar.hidden = true;
        return;
      }

      bar.hidden = false;

      const remaining = Math.max(threshold - cartTotal, 0);
      const percentage = Math.min(
        Math.max((cartTotal / threshold) * 100, 0),
        100
      );

      const message = bar.querySelector(
        '[data-shipping-progress-message]'
      );

      const fill = bar.querySelector(
        '[data-shipping-progress-fill]'
      );

      const progressTrack = bar.querySelector(
        '[role="progressbar"]'
      );

      if (cartTotal >= threshold) {
        bar.classList.add('is-complete');

        if (message) {
          message.textContent = 'You’ve unlocked FREE shipping!';
        }
      } else {
        bar.classList.remove('is-complete');

        if (message) {
          message.innerHTML =
            `Add <strong>${formatMoney(remaining, currency)}</strong> ` +
            'more for FREE shipping.';
        }
      }

      if (fill) {
        fill.style.width = `${percentage}%`;
      }

      if (progressTrack) {
        progressTrack.setAttribute(
          'aria-valuenow',
          String(Math.min(cartTotal, threshold))
        );

        progressTrack.setAttribute(
          'aria-valuemax',
          String(threshold)
        );
      }

      bar.dataset.cartTotal = String(cartTotal);
    });
  }

  /**
   * Retrieves the latest cart state from Shopify.
   */
  async function fetchCart() {
    if (requestInProgress) {
      updateRequested = true;
      return;
    }

    requestInProgress = true;

    try {
      const response = await fetch(CART_ENDPOINT, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Unable to retrieve cart: ${response.status}`
        );
      }

      const cart = await response.json();
      renderProgressBars(cart);
    } catch (error) {
      console.warn(
        'Free shipping progress bar could not update.',
        error
      );
    } finally {
      requestInProgress = false;

      if (updateRequested) {
        updateRequested = false;
        scheduleUpdate(100);
      }
    }
  }

  /**
   * Prevents excessive requests during rapid quantity changes.
   *
   * @param {number} delay
   */
  function scheduleUpdate(delay = 150) {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(fetchCart, delay);
  }

  /**
   * Update after cart-related clicks and input changes.
   */
  document.addEventListener('click', (event) => {
    const cartControl = event.target.closest(
      [
        '[data-add-to-cart]',
        '[name="add"]',
        '[data-cart-remove]',
        '[data-cart-product-remove]',
        '.js-qty__adjust',
        '.cart__remove',
        'button[name="plus"]',
        'button[name="minus"]'
      ].join(',')
    );

    if (cartControl) {
      scheduleUpdate(400);
    }
  });

  document.addEventListener('change', (event) => {
    if (
      event.target.matches(
        [
          'input[name="updates[]"]',
          '[data-qty-input]',
          '.cart__product-qty'
        ].join(',')
      )
    ) {
      scheduleUpdate(400);
    }
  });

  document.addEventListener('submit', (event) => {
    if (
      event.target.matches(
        'form[action*="/cart/add"], form[action*="/cart"]'
      )
    ) {
      scheduleUpdate(500);
    }
  });

  /*
   * Impulse may replace portions of the cart drawer after an Ajax
   * operation. Watching for inserted progress-bar markup ensures
   * the newly rendered copy gets the current cart total.
   */
  const observer = new MutationObserver((mutations) => {
    const relevantChange = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) {
          return false;
        }

        return (
          node.matches?.(SELECTOR) ||
          node.querySelector?.(SELECTOR) ||
          node.matches?.('[data-cart-drawer], #CartDrawer') ||
          node.querySelector?.('[data-cart-drawer], #CartDrawer')
        );
      });
    });

    if (relevantChange) {
      scheduleUpdate(100);
    }
  });

  function initialize() {
    fetchCart();

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  /*
   * Public event for other custom scripts:
   *
   * document.dispatchEvent(
   *   new CustomEvent('aac:cart-updated')
   * );
   */
  document.addEventListener('aac:cart-updated', fetchCart);
})();